import {
    GetRosterMappingsQuery,
    RosterStatus,
    RosterType,
} from '__generated__/graphql'
import ClientIdentityProvider from 'client/contexts/ClientIdentityContext'
import { GetRosterMappings } from 'client/graphql/search.gql'
import { useIdentity } from 'client/hooks'
import { clientRouteConfig } from 'client/shared/routeTargets'
import RostersByStatusList from 'shared/components/RostersByStatusList'
import { usePagination } from 'shared/hooks/usePagination'
import { usePageTitle } from 'shared/hooks/usePageTitle'
import { GroupedStatus, RosterResults } from 'shared/models/groupedStatus'
import { ClientVisibleProcessStatusGroups } from '../RosterSearches/CandidateList/getRosterSearchPreviewProps'
import { RosterListProps } from '../RosterSearches/ClientRosterCardPreview'
import { DetailsRowProps } from '../RosterSearches/DetailsRow'
import ClientMappingPreviewCard from './ClientMappingPreviewCard'

const defaultPageLimit = 10
const savedFilterToken = 'client.searches.activeFilter'
const trackedClassName = 'roster-search-preview'

const RosterMappings: React.FC = () => {
    usePageTitle('Mappings')
    const identity = useIdentity()

    const activeRosterPagination = usePagination<
        GetRosterMappingsQuery['rosterMappings']['data'][number]
    >({
        query: GetRosterMappings,
        variables: {
            rosterStatuses: [RosterStatus.Active],
            limit: defaultPageLimit,
        },
        queryPath: 'rosterMappings',
        limit: defaultPageLimit,
        trackedDivClassName: `${trackedClassName}-active`,
    })

    const closedRosterPagination = usePagination<
        GetRosterMappingsQuery['rosterMappings']['data'][number]
    >({
        query: GetRosterMappings,
        variables: {
            rosterStatuses: [RosterStatus.Closed],
            limit: defaultPageLimit,
        },
        queryPath: 'rosterMappings',
        limit: defaultPageLimit,
        trackedDivClassName: `${trackedClassName}-closed`,
    })

    const getPaginationForFilter = (filter: GroupedStatus) => {
        switch (filter.status) {
            case RosterStatus.Closed:
                return {
                    ...closedRosterPagination,
                    trackedDivClassName:
                        closedRosterPagination.trackedDivClassName ||
                        trackedClassName,
                }
            case RosterStatus.Active:
            default:
                return {
                    ...activeRosterPagination,
                    trackedDivClassName:
                        activeRosterPagination.trackedDivClassName ||
                        trackedClassName,
                }
        }
    }

    const getCards = (data: RosterResults[number]) => {
        return (
            <ClientMappingPreviewCard {...getRosterMappingPreviewProps(data)} />
        )
    }

    return (
        <ClientIdentityProvider>
            <RostersByStatusList
                statuses={[
                    {
                        status: RosterStatus.Active,
                    },
                    {
                        status: RosterStatus.Closed,
                    },
                ]}
                pageTitle={identity?.client?.company?.value ?? ''}
                getPaginationForFilter={getPaginationForFilter}
                saveFilterKey={savedFilterToken}
            >
                {getCards}
            </RostersByStatusList>
        </ClientIdentityProvider>
    )
}

const getRosterMappingPreviewProps = (
    rosterData:
        | GetRosterMappingsQuery['rosterMappings']['data'][number]
        | undefined
): RosterListProps => {
    const details: DetailsRowProps = {
        sharedRecruiters:
            rosterData?.sharedRecruiters?.map(({ recruiter }) => ({
                id: recruiter?.id || '',
                preferredName: recruiter?.user?.preferredName || '',
                profileImageKey: recruiter?.user?.profileImageKey || '',
            })) || [],
        sharedClients:
            rosterData?.sharedClients?.map(({ client }) => ({
                id: client?.id || '',
                preferredName: client?.user?.preferredName || '',
                profileImageKey: client?.user?.profileImageKey || '',
            })) || [],
        positions:
            rosterData?.placements?.map(
                (placement) => placement?.title?.value ?? ''
            ) || [],
        startDateLabel: rosterData?.startDateLabel ?? '',
        officeLocations:
            rosterData?.companyOffices?.map(
                (office) => office?.companyOffice?.officeLocation?.value ?? ''
            ) || [],
        industries: rosterData?.industries?.map(
            (industry) => industry?.industry?.value ?? ''
        ),
        sectors: rosterData?.sectors?.map(
            (sector) => sector?.sector?.value ?? ''
        ),
        hideOnShrink: true,
        createdAt: rosterData?.createdAt ?? '',
        createdBy: rosterData?.createdByUser?.preferredName ?? '',
    }
    return {
        details,
        rosterName: rosterData?.rosterName ?? '',
        rosterType: rosterData?.rosterType ?? RosterType.Mapping,
        navigationTarget: `${clientRouteConfig.mappings.path}`,
        rosterId: rosterData?.id ?? '',
        statusGroupsForGraph: ClientVisibleProcessStatusGroups,
        isSelectable: rosterData?.status === RosterStatus.Active,
        showRosterType: false,
    }
}

export default RosterMappings
